import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { Portal } from '@chakra-ui/react';
import { Button, Divider, Input } from '@sketch-note/design-system/src/components';
import { Navigation } from '@sketch-note/design-system/src/shared/site-settings';
import { CloseIcon, GoogleIcon, SketchNoteLogoIcon } from '@sketchnote/icons';
import { OnlyDesktop } from '@sketchnote/landing-page';
import cn from 'classnames';
import { frontendHostUrl } from 'config';
import { setCookie } from 'cookies-next';
import isElectron from 'is-electron';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/client';
import { NextSeo } from 'next-seo';
import * as React from 'react';
import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { withRoute } from 'src/hoc';
import { AuthFlowLayout } from 'src/layouts/auth-flow/auth-flow.layout';
import { PageMetaData } from 'src/utils/meta-data';

import ForgotPassword from '../signin/forgot-password.page';

const { useState } = React;

export function ErrorContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn({
        'text-center px-24 py-16 bg-red-100 text-red-900 font-medium text-sm rounded': true,
        [className || '']: !!className,
      })}
    >
      {children}
    </div>
  );
}
function SignUp() {
  const { register, handleSubmit, setError, control, setValue } = useForm();
  const { errors } = useFormState({ control });
  const [session, loading] = useSession();
  const [userLoginAccess, setUserLoginAccess] = useState({ success: false, type: 'default' });
  const [email, setEmail] = useState('');
  const [invalidCode, setInvalidCode] = useState(false);

  const [magicLinkConfirmation, setMagicLinkConfirmation] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [forgotpassword, setForgotPassword] = useState(false);

  const router = useRouter();
  const onCheckEmail = async (values) => {
    setEmail(values.email);
    if (!userLoginAccess.success && userLoginAccess.type === 'register') {
      //Login code verification
      const response = await API.post(endpoints.magicVerify, {
        code: values.passcode,
        email: values.email,
      });

      if (response.data.success) {
        setInvalidCode(false);
        router.push(`/signup/register/${response?.data.data}`);
      } else {
        setInvalidCode(true);
      }
    } else {
      //Email verfication

      const response = await API.post(endpoints.loginCheck, {
        ...values,
      });
      setUserLoginAccess({ success: response?.data.success, type: response?.data.type });
    }
  };

  const onSubmit = async (values) => {
    setEmail(values.email);
    signIn('login', {
      email: values.email,
      password: values.password,
      // The page where you want to redirect to after a
      // successful login
      callbackUrl: `${frontendHostUrl}dashboard`,
      redirect: false,
    }).then((res) => {
      const response = res as any;

      if (response && !response.ok) {
        setInvalidPassword(true);
      } else setInvalidPassword(false);
    });

    if (isElectron()) {
      const ipcRenderer = window.require('electron').ipcRenderer;
      ipcRenderer.send('authenticated', 'yes');
    }
  };

  const handleMagicLink = async () => {
    Analytics.track('Get SignIn Magic-link Click');
    const response = await API.post(endpoints.sendMagicLink, {
      email: email,
    });
    if (response?.data.success) {
      setMagicLinkConfirmation(true);
    }
  };
  useEffect(() => {
    // Prefetch the dashboard page
    router.prefetch(Navigation.Dashboard.href);
  }, []);
  return (
    <div>
      <Portal>
        <div className="top-0 left-0 bg-white z-2000 fixed overflow-auto w-screen h-screen xxxs:hidden">
          <OnlyDesktop />
        </div>
      </Portal>
      <div className="absolute top-16 left-65">
        <button onClick={() => router.push('https://sketchnote.co/')}>
          <SketchNoteLogoIcon className="text-primary-500 text-4xl inline-block" />
          <span className="text-primary-800 font-extraBold ml-4 text-xl inline-block">
            sketchnote
          </span>
        </button>
      </div>
      <div className="w-336 mx-auto">
        <img
          src="/images/login-banner-girl-enjoy.png"
          width="164px"
          height="164px"
          className="mx-auto"
          alt="sign up banner"
        />
        <h2 className="mb-14 leading-40 text-center">Welcome to Sketchnote</h2>
        {/* {!userLoginAccess.success && (
          <p className="mb-42 text-center">
            Don’t have an account?
            <Button status="ghost" className="pl-8">
              <Link href={'https://sketchnote.typeform.com/join-waitlist'}>Join the waitlist</Link>
            </Button>
          </p> 
        )}*/}
        {!userLoginAccess.success && (
          <Button
            status="secondary-gray"
            className="w-full mb-20 justify-center"
            size="lg"
            iconLeft={<GoogleIcon />}
            onClick={() => {
              Analytics.track('Sign Up Click');
              const org = localStorage.getItem('invitedOrg');
              if (org) {
                setCookie('org', org, {
                  maxAge: 60 * 60,
                  path: '/',
                });
              }
              signIn('google');
            }}
          >
            Sign up with Google
          </Button>
        )}

        {!userLoginAccess.success && (
          <div className="mb-16 italic text-lg">
            <Divider text="or" />
          </div>
        )}

        {session?.isWaitList || userLoginAccess.type === 'whitelist' ? (
          <p className="px-28 py-16 bg-blue-100 text-sm text-primary-500 mb-10 font-medium">
            One more step before we let you in! Join our waitlist and we’ll get back to you soon
          </p>
        ) : null}

        {!userLoginAccess.success && (
          <form onSubmit={handleSubmit(onCheckEmail)}>
            <Input
              placeholder="Email address"
              field="text"
              textSecClassName="mb-16"
              inputProps={{
                ...register('email', {
                  required: true,
                  onChange: (e) => {
                    setUserLoginAccess({ success: false, type: 'default' });
                    setEmail(e.target.value);
                    setValue('passcode', '');
                    setValue('email', e.target.value);
                    setInvalidCode(false);
                  },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }),
              }}
              status={errors.email ? 'danger' : undefined}
              caption={errors?.email?.message}
              onRightIconClick={() => {
                setEmail('');
                setInvalidCode(false);
                setValue('email', '');
                setValue('passcode', '');
                setUserLoginAccess({ success: false, type: 'default' });
              }}
              rightIcon={email ? <CloseIcon wrapperClass="text-xs text-gray-400" /> : undefined}
            />

            {!userLoginAccess.success && userLoginAccess.type === 'register' && email && (
              <div>
                {' '}
                <p className="text-gray-500 text-xs text-center my-16">
                  We just sent you a temporary login code.
                  <br /> Please check your inbox
                </p>
                <Input
                  placeholder="Paste code here"
                  field="text"
                  textSecClassName="mb-16"
                  inputProps={{
                    ...register('passcode', {
                      required: true,
                    }),
                  }}
                  status={errors.email ? 'danger' : undefined}
                  caption={errors?.email?.message}
                />
                {invalidCode && (
                  <ErrorContent className="mb-16">That’s an invalid code. Try again!</ErrorContent>
                )}
              </div>
            )}
            <Button
              status="primary"
              className="w-full justify-center mb-34"
              size="lg"
              type="submit"
            >
              Continue with email
            </Button>
          </form>
        )}

        {userLoginAccess.type === 'register' && userLoginAccess.success && (
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Input
                placeholder="Email address"
                field="text"
                textSecClassName="mb-16"
                inputProps={{
                  ...register('email', {
                    required: true,
                    onBlur: () => {
                      null;
                    },
                    onChange: () => {
                      setEmail('');
                      setValue('password', '');
                      setValue('email', '');
                      setInvalidPassword(false);
                      setUserLoginAccess({ success: false, type: 'default' });
                    },
                  }),
                }}
                onRightIconClick={() => {
                  setEmail('');
                  setValue('email', '');
                  setValue('password', '');
                  setInvalidPassword(false);
                  setUserLoginAccess({ success: false, type: 'default' });
                }}
                rightIcon={<CloseIcon wrapperClass="text-xs text-gray-400" />}
                status={errors.email ? 'danger' : undefined}
                caption={errors?.email?.message}
              />

              <Input
                placeholder="Password"
                field="text"
                textSecClassName="mb-14"
                type="password"
                inputProps={{
                  ...register('password', {
                    required: true,
                  }),
                }}
                status={errors.password ? 'danger' : undefined}
                caption={errors?.password?.message}
              />
              {invalidPassword && (
                <ErrorContent className="mb-16 -ml-4">
                  Whoops, incorrect password! Try again
                </ErrorContent>
              )}

              <button
                type="button"
                className="mt-10 text-sm mb-32 text-gray-600"
                onClick={() => setForgotPassword(true)}
              >
                Forgot passsword?
              </button>
              <button
                className="w-full py-13 text-center justify-center mb-28 border font-medium text-blue-500 border-blue-500"
                type="submit"
              >
                Continue
              </button>
            </form>
            <div className="mb-28 italic text-lg">
              <Divider text="or" />
            </div>
            <Button
              status="primary"
              className="w-full justify-center mb-34"
              size="lg"
              onClick={handleMagicLink}
              type="submit"
            >
              Get magic link
            </Button>
          </div>
        )}
        <p className="text-center text-xss text-gray-600 mb-16">
          By clicking on continue, you are indicating that you have read and agree to our{' '}
          <Link href={'/terms-of-service'}>
            <a className="font-medium" href="/terms-of-service">
              Terms of Service
            </a>
          </Link>{' '}
          and{' '}
          <Link href="/privacy-policy">
            <a className="font-medium">Privacy Policy</a>
          </Link>
        </p>
        <p className="text-gray-500 mb-34 text-center text-xs">
          <Link href={'https://sketchnote.co/forms/636cf35095845f348e2221c8'}>
            <a target="_blank">Contact support</a>
          </Link>
        </p>
      </div>
      <Modal
        isOpen={magicLinkConfirmation}
        onClose={() => {
          setMagicLinkConfirmation(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{ maxWidth: '38rem', padding: '32px', marginTop: '30rem', borderRadius: '8px' }}
        >
          <p className="mb-16 font-medium text-2xl">You&apos;ve got mail!</p>
          <p className="mb-32 text-sm text-gray-600">
            <ModalCloseButton />
            We&apos;ve sent a login link to {email}.<br />
            Click on it to start using Sketchnote!
          </p>
          <button
            className="self-end bg-blue-500 text-center text-white py-13 px-24 rounded-sm font-medium w-86"
            onClick={() => {
              setMagicLinkConfirmation(false);
            }}
          >
            Okay
          </button>
        </ModalContent>
      </Modal>

      <Modal isOpen={forgotpassword} closeOnEsc={false} onClose={() => null}>
        <ModalOverlay />
        <ModalContent
          maxWidth="100vw"
          height="100vh"
          className="bg-gray-100 static rounded-lg"
          margin="auto"
        >
          <ModalBody
            className="flex items-center justify-center text-left p-32 bg-gray-100"
            borderRadius="0.8rem"
          >
            <div className="overflow-hidden">
              <ForgotPassword />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
SignUp.layout = AuthFlowLayout;

SignUp.MetaData = <NextSeo {...PageMetaData.dashboard} />;

export const getStaticProps: GetStaticProps = async (context) => {
  return { props: {} };
};

export default withRoute(SignUp);
